import { useState } from 'react';
import { alpha, Button, Divider, Menu, MenuItem } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ALL_SHOPS,
  DEFAULT_ORDER_FILTER,
  DEFAULT_SORT_KEY,
  ORDER_FILTER_PRESETS,
  OrderFilterKey,
  OrderSortKey,
  ShopFilterKey,
  SORT_PRESETS,
} from '../utils/orderFilters';

type Props = {
  orderFilter: OrderFilterKey;
  onOrderFilterChange: (key: OrderFilterKey) => void;
  sortKey: OrderSortKey;
  onSortKeyChange: (key: OrderSortKey) => void;
  /**
   * The restaurants to offer a choice between, `ALL_SHOPS` first. Omitted — or holding a single
   * restaurant — and no restaurant pill appears: a filter with one thing to pick is just a label.
   */
  shopOptions?: PillOption[];
  shopFilter?: ShopFilterKey;
  /**
   * The chosen option's raw `value`, since the pill's menu is generic over plain strings. Turning it
   * back into a `ShopFilterKey` is the caller's to do — `asShopFilterKey` in `orderFilters` — which
   * keeps the one unchecked crossing at the edge that owns the state rather than in here.
   */
  onShopFilterChange?: (value: string) => void;
};

/**
 * The controls above the list, right-aligned so the eye lands on the orders first. Each one is a filter
 * pill that opens its choices in a menu — it costs one line however many choices it holds, and it reads
 * as a filter rather than as a form field waiting to be filled in.
 *
 * Each pill shows its current choice rather than the filter's name, so the bar answers "which orders am
 * I looking at" without being opened — including when the choice is the default, which named as a bare
 * filter label read as though nothing had been chosen at all. `active` tints the pill when the choice
 * actually narrows the list.
 *
 * The sort pill is hidden from 600px up, where the DataGrid sorts by column header — a second control
 * the grid could silently disagree with is worse than no control.
 */
export const OrdersFilterBar: React.FC<Props> = ({
  orderFilter,
  onOrderFilterChange,
  sortKey,
  onSortKeyChange,
  shopOptions,
  shopFilter = ALL_SHOPS,
  onShopFilterChange,
}) => {
  // Two entries is one restaurant plus "all of them" — nothing to choose between.
  const hasShopChoice = !!shopOptions && shopOptions.length > 2 && !!onShopFilterChange;

  return (
    <Bar>
      {/* First, because it is the widest cut: which restaurant, then which of its orders. */}
      {hasShopChoice && (
        <FilterPill
          options={shopOptions}
          value={shopFilter}
          active={shopFilter !== ALL_SHOPS}
          onChange={onShopFilterChange}
        />
      )}
      <FilterPill
        options={ORDER_FILTER_PRESETS}
        value={orderFilter}
        active={orderFilter !== DEFAULT_ORDER_FILTER}
        onChange={(value) => onOrderFilterChange(value as OrderFilterKey)}
      />
      <SortPill
        options={SORT_PRESETS}
        value={sortKey}
        active={sortKey !== DEFAULT_SORT_KEY}
        onChange={(value) => onSortKeyChange(value as OrderSortKey)}
      />
    </Bar>
  );
};

/**
 * One choice in a pill's menu. `labelKey` for the fixed presets, which are translated; `label` for
 * names that arrive as data — a restaurant's, say — and are already in their final form. `label`
 * wins where both are given.
 */
export type PillOption = { value: string; labelKey?: string; label?: string; startsGroup?: boolean };

type PillProps = {
  options: PillOption[];
  value: string;
  /** Tints the pill, marking the filter as narrowing the list. */
  active: boolean;
  onChange: (value: string) => void;
  className?: string;
};

const FilterPill: React.FC<PillProps> = ({ options, value, active, onChange, className }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = options.find((option) => option.value === value);

  const labelOf = (option: PillOption): string => option.label ?? (option.labelKey ? t(option.labelKey) : '');

  return (
    <>
      <Pill
        className={className}
        $active={active}
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {selected && labelOf(selected)}
      </Pill>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {options.flatMap((option) => [
          // `component="li"` keeps the divider a legal child of the menu's own <ul>.
          ...(option.startsGroup ? [<Divider key={`${option.value}-group`} component="li" />] : []),
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setAnchorEl(null);
            }}
          >
            {/* The slot is always there, so the labels line up whether or not they carry the tick. */}
            <CheckSlot>{option.value === value && <CheckIcon fontSize="small" color="primary" />}</CheckSlot>
            {labelOf(option)}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Pill = styled(Button)<{ $active: boolean }>`
  && {
    border-radius: 999px;
    padding: 4px 12px;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: 500;
    text-transform: none;
    white-space: nowrap;
    border-color: ${({ theme, $active }) => ($active ? theme.palette.primary.main : theme.palette.divider)};
    background-color: ${({ theme, $active }) => ($active ? alpha(theme.palette.primary.main, 0.1) : 'transparent')};
    color: ${({ theme, $active }) => ($active ? theme.palette.primary.main : theme.palette.text.primary)};

    .MuiButton-endIcon {
      margin-left: 2px;
      color: ${({ theme }) => theme.palette.text.secondary};
    }
  }
`;

/** Sized to the small icon it holds, so the tick sits in a column of its own rather than indenting text. */
const CheckSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

// `&&` so the rule outweighs MUI Button's own `display: inline-flex`, which sits at one class.
const SortPill = styled(FilterPill)`
  && {
    ${({ theme }) => theme.breakpoints.up('sm')} {
      display: none;
    }
  }
`;
